import { TrackedMap } from 'tracked-built-ins';
import { encodeFileToBase64, sanitizeFilename } from 'client-portal/utils/prescreener/upload-file';
import Service from '@ember/service';

const getFileComparator =
  (questionName, type, side = null) =>
  ({ metadata }) =>
    metadata.question_name === questionName &&
    metadata.type === type &&
    (!side || metadata.side === side);

export default class PrescreenerUploadService extends Service {
  files = new TrackedMap();

  get filesToUpload() {
    return Array.from(this.files.values())
      .flat()
      .map(({ uploadFile, metadata }) => ({
        file: uploadFile?.file,
        metadata,
      }));
  }

  getFile({ questionName, clientType, side }) {
    if (!this.files.get(questionName)) return null;

    let fileWithMetadata = this.files
      .get(questionName)
      .find(getFileComparator(questionName, clientType, side));
    return fileWithMetadata?.uploadFile;
  }

  addFile(uploadFile, metadata) {
    const { 'question_name': questionName, type, side } = metadata;
    const fileComparator = getFileComparator(questionName, type, side);
    const existingFiles = this.files.get(questionName) ?? [];

    const updatedFiles = uploadFile
      ? this.#addOrUpdateFile({ existingFiles, uploadFile, metadata, fileComparator })
      : this.#removeFile(existingFiles, fileComparator);

    this.files.set(questionName, updatedFiles);
  }

  addFileForQuestion({ questionName, clientType, side, fileUpload }) {
    let mataData = this.createMetaData({ questionName, type: clientType, side, fileUpload });
    this.addFile(fileUpload, mataData);
  }

  createMetaData({ questionName, type, side, fileUpload }) {
    return {
      filename: sanitizeFilename(fileUpload.file.name),
      'question_name': questionName,
      'question_type': 'file',
      type,
      side,
    };
  }

  async getBase64EncodedFiles() {
    let fileGroups = Array.from(this.files.values()).flat();

    let encoded = await Promise.all(
      fileGroups.map(async ({ uploadFile, metadata }) => {
        if (!uploadFile?.file) return null;

        return await encodeFileToBase64(uploadFile.file, metadata);
      })
    );

    return encoded.filter(Boolean);
  }

  async getEncodedAnswers(answerModel) {
    let file = await this.getBase64EncodedFiles();
    answerModel.set('files', file);
    return answerModel;
  }

  #addOrUpdateFile({ existingFiles, uploadFile, metadata, fileComparator }) {
    const existingFile = existingFiles.find(fileComparator);

    if (existingFile) {
      existingFile.uploadFile = uploadFile;
      existingFile.metadata = metadata;
      return existingFiles;
    }

    const newFile = {
      metadata,
      uploadFile,
    };

    return existingFiles.length ? [...existingFiles, newFile] : [newFile];
  }

  #removeFile(existingFiles, fileComparator) {
    return existingFiles.filter(fileWithMetadata => !fileComparator(fileWithMetadata));
  }
}
